import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getInvoiceListAction,
  getQBOAuthAction,
  setRedirectURLAction,
} from 'store/actions/invoiceActions';
import { InvoiceTable } from 'components/InvoiceTable';
import { InvoiceDateRangeFilterFields } from 'components/InvoiceTable/InvoiceDateRangeFilterFields';
import { Button, Dropdown, Loading } from '@leapeasy/ui-kit';
import { readAuthSession } from 'utils/authSession';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  openQuickBooksOAuthPopup,
  resolveRedirectUrl,
  shouldShowQuickBooksConnect,
} from 'utils/quickBooksOAuth';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { useNavigate, useLocation } from 'react-router-dom';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { usePaginatedListFetch } from 'hooks/usePaginatedListFetch';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { getInvoiceList } from 'store/sagas/APIs/invoice';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildInvoiceListFilterChips,
  parseInvoiceListFilterChipId,
} from 'utils/listTableActiveFilterChips';
import {
  DEFAULT_INVOICE_LIST_EXTRA_FILTERS,
  INVOICE_LIST_SEARCH_PLACEHOLDER,
  INVOICE_DISPUTED_FILTER_OPTIONS,
  areArraysEqual,
  areInvoiceDateRangeFiltersEqual,
  buildInvoiceListRequestFilter,
  parseInvoiceFilters,
  serializeInvoiceFilters,
} from 'utils/invoiceListQueryFilters';
import { EMPTY_INVOICE_DATE_RANGE_FILTER } from 'utils/invoiceDateRangeFilter';
import {
  fetchInvoiceListForExport,
  mapInvoiceListSortOptionsForApi,
} from 'utils/invoiceListExport';
import { buildDetailNavigationState } from 'utils/detailBackNavigation';

const EMPTY_INVOICE_EXPORT_FILTER = buildInvoiceListRequestFilter({
  filter: {
    options: [],
    searchText: '',
    searchPlaceholder: 'Search Property..',
  },
  selectedPayment: [],
  selectedInvoiceStatus: [],
  selectedDueDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  selectedInvoiceDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  selectedDisputed: [],
});

export const Invoice = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
    isHydrated,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: INVOICE_LIST_SEARCH_PLACEHOLDER,
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_INVOICE_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseInvoiceFilters,
    serializeExtraFilters: serializeInvoiceFilters,
  });
  const [selectedPayment, setSelectedPayment] = useState([]);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState([]);
  const [selectedDueDateRange, setSelectedDueDateRange] = useState(() => ({
    ...EMPTY_INVOICE_DATE_RANGE_FILTER,
  }));
  const [selectedInvoiceDateRange, setSelectedInvoiceDateRange] = useState(() => ({
    ...EMPTY_INVOICE_DATE_RANGE_FILTER,
  }));
  const [selectedDisputed, setSelectedDisputed] = useState([]);
  const [selectedPaymentDraft, setSelectedPaymentDraft] = useState([]);
  const [selectedInvoiceStatusDraft, setSelectedInvoiceStatusDraft] = useState([]);
  const [selectedDueDateRangeDraft, setSelectedDueDateRangeDraft] = useState(() => ({
    ...EMPTY_INVOICE_DATE_RANGE_FILTER,
  }));
  const [selectedInvoiceDateRangeDraft, setSelectedInvoiceDateRangeDraft] = useState(() => ({
    ...EMPTY_INVOICE_DATE_RANGE_FILTER,
  }));
  const [selectedDisputedDraft, setSelectedDisputedDraft] = useState([]);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedPayment = Array.isArray(extraFilters.selectedPayment)
      ? extraFilters.selectedPayment
      : [];
    const nextSelectedInvoiceStatus = Array.isArray(extraFilters.selectedInvoiceStatus)
      ? extraFilters.selectedInvoiceStatus
      : [];
    const nextSelectedDueDateRange = extraFilters.selectedDueDateRange || {
      ...EMPTY_INVOICE_DATE_RANGE_FILTER,
    };
    const nextSelectedInvoiceDateRange = extraFilters.selectedInvoiceDateRange || {
      ...EMPTY_INVOICE_DATE_RANGE_FILTER,
    };
    const nextSelectedDisputed = Array.isArray(extraFilters.selectedDisputed)
      ? extraFilters.selectedDisputed
      : [];
    setSelectedPayment((prev) =>
      areArraysEqual(prev, nextSelectedPayment) ? prev : nextSelectedPayment,
    );
    setSelectedInvoiceStatus((prev) =>
      areArraysEqual(prev, nextSelectedInvoiceStatus) ? prev : nextSelectedInvoiceStatus,
    );
    setSelectedDueDateRange((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedDueDateRange)
        ? prev
        : nextSelectedDueDateRange,
    );
    setSelectedInvoiceDateRange((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedInvoiceDateRange)
        ? prev
        : nextSelectedInvoiceDateRange,
    );
    setSelectedDisputed((prev) =>
      areArraysEqual(prev, nextSelectedDisputed) ? prev : nextSelectedDisputed,
    );
    setSelectedPaymentDraft((prev) =>
      areArraysEqual(prev, nextSelectedPayment) ? prev : nextSelectedPayment,
    );
    setSelectedInvoiceStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedInvoiceStatus) ? prev : nextSelectedInvoiceStatus,
    );
    setSelectedDueDateRangeDraft((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedDueDateRange)
        ? prev
        : nextSelectedDueDateRange,
    );
    setSelectedInvoiceDateRangeDraft((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedInvoiceDateRange)
        ? prev
        : nextSelectedInvoiceDateRange,
    );
    setSelectedDisputedDraft((prev) =>
      areArraysEqual(prev, nextSelectedDisputed) ? prev : nextSelectedDisputed,
    );
  }, [extraFilters]);

  const listRequestFilter = useMemo(
    () =>
      buildInvoiceListRequestFilter({
        filter,
        selectedPayment: Array.isArray(extraFilters?.selectedPayment)
          ? extraFilters.selectedPayment
          : [],
        selectedInvoiceStatus: Array.isArray(extraFilters?.selectedInvoiceStatus)
          ? extraFilters.selectedInvoiceStatus
          : [],
        selectedDueDateRange: extraFilters?.selectedDueDateRange || {
          ...EMPTY_INVOICE_DATE_RANGE_FILTER,
        },
        selectedInvoiceDateRange: extraFilters?.selectedInvoiceDateRange || {
          ...EMPTY_INVOICE_DATE_RANGE_FILTER,
        },
        selectedDisputed: Array.isArray(extraFilters?.selectedDisputed)
          ? extraFilters.selectedDisputed
          : [],
      }),
    [filter, extraFilters],
  );

  const resolvedSortOptions = useMemo(
    () => mapInvoiceListSortOptionsForApi(sortOptions),
    [sortOptions],
  );

  const listQuery = useMemo(
    () => ({
      filter: listRequestFilter,
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions: resolvedSortOptions,
    }),
    [listRequestFilter, pagination.pageNumber, pagination.rowsPerPage, resolvedSortOptions],
  );

  usePaginatedListFetch({
    enabled: isHydrated,
    action: getInvoiceListAction,
    payload: listQuery,
  });

  const refetchList = useCallback(() => {
    dispatch(getInvoiceListAction(listQuery));
  }, [dispatch, listQuery]);

  const fetchFullExportDatasetTotal = useCallback(async () => {
    const response = await getInvoiceList({
      filter: EMPTY_INVOICE_EXPORT_FILTER,
      offset: 0,
      limit: 1,
      sortOptions: resolvedSortOptions,
    });
    return response?.total != null ? Number(response.total) : undefined;
  }, [resolvedSortOptions]);

  const {
    fullExportDatasetTotal,
    isLoadingFullExportTotal,
    loadFullExportDatasetTotal,
    resetFullExportDatasetTotal,
  } = useLazyExportDatasetTotal(fetchFullExportDatasetTotal);

  useEffect(() => {
    resetFullExportDatasetTotal();
  }, [resolvedSortOptions, resetFullExportDatasetTotal]);

  const invoices = useSelector((state) => state.getIn(['invoice', 'invoiceData']));
  const redirectURL = useSelector((state) => state.getIn(['invoice', 'redirectURL']));
  const invoicesJSON = invoices?.toJS?.() ?? null;
  const invoiceListError = invoicesJSON?.invoiceListError;
  const invoiceTotal = Number(invoicesJSON?.total) || 0;

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: invoicesJSON?.total,
    isLoading: Boolean(invoicesJSON?.isLoading),
  });

  const [isQboConnecting, setIsQboConnecting] = useState(false);
  const { user } = readAuthSession();
  const showQuickBooksConnect = shouldShowQuickBooksConnect({
    role: user?.role,
    authToken: invoicesJSON?.auth_token,
    quickbooksConnected: invoicesJSON?.quickbooks_connected,
  });

  useEffect(() => {
    const url = resolveRedirectUrl(redirectURL);
    if (!url) return;

    setIsQboConnecting(false);
    const popup = openQuickBooksOAuthPopup(url);
    dispatch(setRedirectURLAction(null));

    if (!popup) {
      dispatch(failedNotif('Pop-up blocked. Allow pop-ups for this site to connect QuickBooks.'));
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (!popup.closed) return;
      window.clearInterval(intervalId);
      refetchList();
    }, 500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [redirectURL, dispatch, refetchList]);

  useEffect(() => {
    if (!isQboConnecting) return undefined;
    const timeoutId = window.setTimeout(() => setIsQboConnecting(false), 20000);
    return () => window.clearTimeout(timeoutId);
  }, [isQboConnecting]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(invoicesJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchInvoiceListForExport({
        filter: listRequestFilter,
        sortOptions,
        totalRecords,
        fetchList: getInvoiceList,
      });
    } catch (e) {
      throw new Error('Unable to export filtered invoices right now.');
    }
  }, [invoicesJSON?.total, listRequestFilter, sortOptions]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(await loadFullExportDatasetTotal()) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchInvoiceListForExport({
        filter: EMPTY_INVOICE_EXPORT_FILTER,
        sortOptions,
        totalRecords,
        fetchList: getInvoiceList,
      });
    } catch (e) {
      throw new Error('Unable to export all invoices right now.');
    }
  }, [fullExportDatasetTotal, loadFullExportDatasetTotal, sortOptions]);

  const applyFunc = useCallback(() => {
    setSelectedPayment(selectedPaymentDraft);
    setSelectedInvoiceStatus(selectedInvoiceStatusDraft);
    setSelectedDueDateRange(selectedDueDateRangeDraft);
    setSelectedInvoiceDateRange(selectedInvoiceDateRangeDraft);
    setSelectedDisputed(selectedDisputedDraft);
    setExtraFilters({
      selectedPayment: selectedPaymentDraft,
      selectedInvoiceStatus: selectedInvoiceStatusDraft,
      selectedDueDateRange: selectedDueDateRangeDraft,
      selectedInvoiceDateRange: selectedInvoiceDateRangeDraft,
      selectedDisputed: selectedDisputedDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedPaymentDraft,
    selectedInvoiceStatusDraft,
    selectedDueDateRangeDraft,
    selectedInvoiceDateRangeDraft,
    selectedDisputedDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedPayment([]);
    setSelectedInvoiceStatus([]);
    setSelectedDueDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedInvoiceDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedDisputed([]);
    setSelectedPaymentDraft([]);
    setSelectedInvoiceStatusDraft([]);
    setSelectedDueDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedInvoiceDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedDisputedDraft([]);
    setExtraFilters({ ...DEFAULT_INVOICE_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const listFilterChips = useMemo(
    () =>
      buildInvoiceListFilterChips({
        selectedPayment,
        selectedInvoiceStatus,
        selectedDueDateRange,
        selectedInvoiceDateRange,
        selectedDisputed,
      }),
    [
      selectedPayment,
      selectedInvoiceStatus,
      selectedDueDateRange,
      selectedInvoiceDateRange,
      selectedDisputed,
    ],
  );

  const handleRemoveInvoiceListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseInvoiceListFilterChipId(chipId);
      if (!parsed) return;

      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));

      if (parsed.type === 'dueDateRange') {
        setSelectedDueDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setSelectedDueDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setExtraFilters((prev) => ({
          ...prev,
          selectedDueDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
        }));
        bumpPage();
        return;
      }
      if (parsed.type === 'invoiceDateRange') {
        setSelectedInvoiceDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setSelectedInvoiceDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setExtraFilters((prev) => ({
          ...prev,
          selectedInvoiceDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
        }));
        bumpPage();
        return;
      }
      if (parsed.type === 'payment') {
        const next = selectedPayment.filter((s) => s !== parsed.value);
        setSelectedPayment(next);
        setSelectedPaymentDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedPayment: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'invoiceStatus') {
        const next = selectedInvoiceStatus.filter((s) => s !== parsed.value);
        setSelectedInvoiceStatus(next);
        setSelectedInvoiceStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedInvoiceStatus: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'disputed') {
        const next = selectedDisputed.filter((s) => s !== parsed.value);
        setSelectedDisputed(next);
        setSelectedDisputedDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedDisputed: next }));
        bumpPage();
      }
    },
    [selectedPayment, selectedInvoiceStatus, selectedDisputed, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveInvoiceListFilterChip,
    resetFunc,
  );

  const onClickInvoice = useCallback(
    (invoice) => {
      const invoiceId = invoice?.invoice_id;
      if (!invoiceId) {
        dispatch(failedNotif('Invoice detail ID is missing.'));
        return;
      }
      dispatch(
        openDetails({
          type: 'invoice',
          data: invoice,
        }),
      );
      const returnTo = `${location.pathname}${location.search}`;
      navigate(`/invoice/${invoiceId}/detail/current`, {
        state: buildDetailNavigationState({}, returnTo),
      });
    },
    [dispatch, navigate, location.pathname, location.search],
  );

  return (
    <DashboardLayoutContainer>
      {invoicesJSON ? (
        <React.Fragment>
          <CustomBox>
            {invoicesJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
            {invoiceListError && !invoicesJSON.isLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
                <GlobalUXState variant="apiError" compact description={invoiceListError} />
                <Button variant="primary" size="medium" onClick={refetchList}>
                  Retry
                </Button>
              </Box>
            ) : null}
            {showQuickBooksConnect && (
              <QuickBooksConnectRow>
                <Button
                  variant="primary"
                  disabled={isQboConnecting}
                  onClick={() => {
                    if (isQboConnecting) return;
                    setIsQboConnecting(true);
                    dispatch(getQBOAuthAction());
                  }}
                >
                  {isQboConnecting ? 'Connecting...' : 'Connect to QuickBooks'}
                </Button>
              </QuickBooksConnectRow>
            )}
            <InvoiceTable
              invoices={invoicesJSON}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={loadFullExportDatasetTotal}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              dropFilterActiveSummary={dropFilterActiveSummary}
              dropFilterActiveCount={dropFilterActiveCount}
              filter={filter}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedPaymentDraft(e.target.value);
                      }}
                      options={[
                        {
                          text: 'Paid',
                          value: '1',
                        },
                        {
                          text: 'Unpaid ',
                          value: '-1',
                        },
                      ]}
                      value={selectedPaymentDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Status"
                      label="Payment"
                      style={{
                        width: '100%',
                      }}
                    />

                    <Dropdown
                      onChange={(e) => {
                        setSelectedInvoiceStatusDraft(e.target.value);
                      }}
                      options={[
                        {
                          text: 'Paid',
                          value: 'Paid',
                        },
                        {
                          text: 'Viewed',
                          value: 'Viewed',
                        },
                        {
                          text: 'Sent',
                          value: 'Sent',
                        },
                      ]}
                      value={selectedInvoiceStatusDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Type"
                      label="Invoice Status"
                      style={{
                        width: '100%',
                      }}
                    />
                    <InvoiceDateRangeFilterFields
                      label="Invoice date"
                      value={selectedInvoiceDateRangeDraft}
                      onChange={setSelectedInvoiceDateRangeDraft}
                    />
                    <InvoiceDateRangeFilterFields
                      label="Due date"
                      value={selectedDueDateRangeDraft}
                      onChange={setSelectedDueDateRangeDraft}
                    />
                    <Dropdown
                      onChange={(e) => {
                        setSelectedDisputedDraft(e.target.value);
                      }}
                      options={INVOICE_DISPUTED_FILTER_OPTIONS}
                      value={selectedDisputedDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All"
                      label="Disputed"
                      style={{
                        width: '100%',
                      }}
                    />
                  </DropFilterContainer>
                ),
                applyFunc: applyFunc,
                resetFunc: resetFunc,
              }}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: invoiceTotal,
                totalPages: Math.ceil(invoiceTotal / pagination.rowsPerPage) || 0,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickInvoice={onClickInvoice}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </DashboardLayoutContainer>
  );
};

const CustomBox = styled.div`
  position: relative;
`;

const QuickBooksConnectRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;

  button {
    width: auto;
    flex: 0 0 auto;
  }
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  * {
    box-sizing: border-box;
  }
`;
